import React, { useEffect, useState } from 'react';

import { Box, Flex, Text, Spinner, useColorMode } from '@vimeo/bokeh';

import { vimeoHCClient } from '../api/vimeoHC';
import { useHelpPanel } from '../hooks/useHelpPanel';
import { useVimeoHC } from '../hooks/useVimeoHC';
import { HelpArticle } from '../types';

export const ArticlePanel: React.FC = () => {
  const { data } = useHelpPanel();
  const initialArticle: HelpArticle = data.article || { id: '', title: '', url: '', content: '' };

  const { loading, error, execute } = useVimeoHC(vimeoHCClient.getArticleById);
  const [fullArticle, setFullArticle] = useState<HelpArticle>(initialArticle);
  const { colorMode } = useColorMode();

  useEffect(() => {
    if (initialArticle.id) {
      const controller = new AbortController();
      execute(initialArticle.id, controller.signal).then((result) => {
        if (result) {
          setFullArticle(result);
        }
      });
      return () => controller.abort();
    }
  }, [initialArticle.id, execute]);

  if (loading) {
    return (
      <Flex direction="column" alignItems="center" justifyContent="center" height="100%">
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4} color="status-destructive-primary">
        <Text>Failed to load article content. Please try again.</Text>
      </Box>
    );
  }

  const isDarkMode = colorMode === 'dark';

  return (
    <Box
      sx={{
        p: {
          fontSize: 'var(--vimeo-fontSizes-heading-xs)',
          lineHeight: 1.5,
          marginBottom: 'md',
          color: isDarkMode ? 'var(--vimeo-colors-gray-100)' : undefined,
        },
        span: {
          color: isDarkMode ? 'var(--vimeo-colors-gray-100) !important' : undefined,
        },
        'h1, h2, h3, h4, h5, h6': {
          marginTop: 'xl',
          marginBottom: 'md',
          fontWeight: 'bold',
          color: isDarkMode ? 'var(--vimeo-colors-gray-100)' : undefined,
        },
        h1: {
          fontSize: '24px',
        },
        h2: {
          fontSize: 'md',
        },
        'ul, ol': {
          marginLeft: 'lg',
          marginBottom: 'md',
          color: isDarkMode ? 'var(--vimeo-colors-gray-100)' : undefined,
        },
        a: {
          color: isDarkMode ? 'var(--vimeo-colors-blue-300)' : 'var(--vimeo-colors-vimeoBlue-600)',
          textDecoration: 'none',
          fontSize: 'var(--vimeo-fontSizes-heading-xs)',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        img: {
          maxWidth: '100%',
          height: 'auto',
        },
        table: {
          maxWidth: '100%',
          height: 'auto',
        },
        iframe: {
          maxWidth: '100%',
        },
      }}
    >
      <div
        data-testid="article-content"
        dangerouslySetInnerHTML={{ __html: fullArticle.content }}
      ></div>
    </Box>
  );
};
